<template>
    <div class="m-children-8 address-fields">
        <CustomInput
            :id="`${id}-id`"
            v-model="localAddress.id"
            label=""
            type="hidden"/>
        <CustomInput
            :id="`${id}-country`"
            v-model="countryInput"
            label="Country/Region"
            requiredError="Select a country"
            type="select">
            <option disabled value="">--</option>
            <option
                v-for="(country, key) in countryNames"
                :key="key"
                :value="country">
                {{ country }}
            </option>
        </CustomInput>
        <CustomInput
            v-if="showFullName"
            :id="`${id}-contactName`"
            v-model="localAddress.contactName"
            label="Full Name"
            requiredError="Enter a full name"
            type="text"/>
        <CustomInput
            :id="`${id}-street1`"
            v-model="localAddress.street1"
            label="Address"
            requiredError="Enter an address"
            type="text"/>
        <div class="d-grid grid-2 gap-8">
            <CustomInput
                :id="`${id}-city`"
                v-model="localAddress.city"
                label="City"
                requiredError="Enter a city"
                type="text"/>
            <template v-if="isCanada(localAddress.country) || isUs(localAddress.country)">
                <CustomInput
                    :id="`${id}-province`"
                    v-model="localAddress.province"
                    :label="isCanada(localAddress.country) ? 'Province' : 'State'"
                    :requiredError="`Enter a ${isCanada(localAddress.country) ? 'province' : 'state'}`"
                    type="select">
                    <option disabled value="">--</option>
                    <option
                        v-for="(provinceStateName, key) in provinceStateList"
                        :key="key"
                        :value="provinceStateName">
                        {{ provinceStateName }}
                    </option>
                </CustomInput>
            </template>
            <template v-else>
                <CustomInput
                    :id="`${id}-province`"
                    v-model="localAddress.province"
                    requiredError="Enter a province/state"
                    label="Province/State"
                    type="text"/>
            </template>
        </div>
        <CustomInput
            :id="`${id}-postalCode`"
            v-model="postalCodeInput"
            :invalidMessage="postalInvalidMessage"
            :label="`${isUs(localAddress.country) ? 'ZIP' : 'Postal'} Code`"
            :requiredError="`Enter a ${isUs(localAddress.country) ? 'ZIP' : 'postal'} code`"
            :errorOnInput="true"
            type="text"
            @blur="postalFocus = false"
            @focus="postalFocus = true"/>
        <slot name="contact-info"/>
        <div v-if="!hideInfo" :class="{ 'd-grid grid-2 gap-8': !isMobile }">
            <CustomInput
                :id="`${id}-email`"
                v-model="localAddress.email"
                :class="{'pb-8': isMobile}"
                :invalidMessage="validateEmail(localAddress.email) ? '' : 'Invalid email'"
                label="Email"
                requiredError="Enter an email"
                :errorOnInput="true"
                type="text"/>
            <CustomInput
                v-if="formatPhone"
                :id="`${id}-phone`"
                v-model="phoneInput"
                :invalidMessage="validatePhoneNumber(localAddress.phone) ? '' : 'Invalid phone number'"
                label="Phone Number"
                type="tel"
                maxlength="12"
                requiredError="Enter a phone number"
                :errorOnInput="true"
                @keypress="handlePhoneKeypress"
                @paste="handlePhonePaste"/>
            <CustomInput
                v-else
                :id="`${id}-phone`"
                v-model="phoneInput"
                label="Phone Number"
                type="tel"
                requiredError="Enter a phone number"
                :errorOnInput="true"
                @keypress="handlePhoneKeypress"
                @paste="handlePhonePaste"/>
        </div>
    </div>
</template>

<script lang="ts" setup>
import CustomInput from '../portal/CustomInput.vue';
import Address from '../../models/Address';
import { computed, withDefaults, ref, watchEffect, onMounted, reactive } from 'vue';
import { useActors, useAddress } from '../../compositions';
import { useScreenDimensions } from '../../compositions/useScreenDimensions';
import { COUNTRIES_NAMES } from '../../scripts/constants';

const emit = defineEmits<{
    (event: 'update', address: Address, valid: boolean): void,
}>();

const props = withDefaults(defineProps<{
    id: string,
    address?: Address,
    showFullName?: boolean,
    countries?: string[],
    formatPhone?: boolean,
    hideInfo?: boolean,
}>(), {
    formatPhone: true,
    address: null,
    showFullName: true,
    countries: null,
    hideInfo: false,
});

const {
    isUs,
    isCanada,
    getProvinceStateArray,
    validatePostalCode,
    validateEmail,
    validatePhoneNumber,
    validateAddress,
    formatPostal,
    formatPhoneNumber,
} = useAddress();

const { merchant } = useActors();
const { isMobile } = useScreenDimensions();

const localAddress = reactive<Address>(props.address);

const postalFocus = ref(false);

const handlePhoneKeypress = (event: KeyboardEvent) => {
    if(isNaN(+event.key)) {
        event.preventDefault();
    }
};

const handlePhonePaste = (event: ClipboardEvent) => {
    const clipboardText = event.clipboardData.getData('text');
    if(!/\d/.test(clipboardText)) {
        event.preventDefault();
    }
    event.clipboardData.setData('text', clipboardText.replace(/\D/g, '').slice(0, 10));
};

const countryNames = computed(() => {
    if(!props.countries?.length) { return COUNTRIES_NAMES; }
    return Object.fromEntries(Object.entries(COUNTRIES_NAMES).filter(([k]) => props.countries.includes(k)));
});

const postalInvalidMessage = computed(() => {
    if(localAddress.postalCode) {
        if(validatePostalCode(localAddress)) { return ''; }
        if(isUs(localAddress.country)) { return 'Invalid ZIP code'; }
        return 'Invalid postal code';
    }
    return '';
});

const provinceStateList = computed(() => {
    const provinceStateArray = getProvinceStateArray(localAddress.country);
    return provinceStateArray ? provinceStateArray : [];
});

const countryInput = computed({
    get() {
        return COUNTRIES_NAMES[localAddress.country];
    },
    set(newValue) {
        localAddress.country = Object.keys(COUNTRIES_NAMES).find((k: string) => COUNTRIES_NAMES[k] === newValue);
        if(!Object.values(provinceStateList.value).includes(localAddress.province)) {
            localAddress.province = '';
        }
        localAddress.postalCode = '';
    },
});

const postalCodeInput = computed({
    get() {
        return postalFocus.value ? localAddress.postalCode : formatPostal(localAddress);
    },
    set(newValue: string) {
        localAddress.postalCode = newValue;
    },
});

const phoneInput = computed({
    get() {
        return props.formatPhone ? formatPhoneNumber(localAddress.phone) : localAddress.phone;
    },
    set(newValue: string) {
        localAddress.phone = newValue.replace(/\D/g, '').slice(0, 10);
    },
});

watchEffect(() => {
    emit('update', localAddress, validateAddress(localAddress, props.showFullName, props.formatPhone));
});

onMounted(() => {
    if(!props.address.country && Object.keys(countryNames.value).includes(merchant.value.address.country)) {
        localAddress.country = merchant.value.address.country;
    }
});
</script>
